/* eslint-disable no-console */
import { getSocket } from '@/socket/socket';
import { defineStore } from 'pinia';
import { useExportStore } from './export';
import { BACKEND_URL } from '@/services/apiClient';

type CaseNoteEventType = 'CASE_NOTE_CREATED' | 'CASE_NOTE_UPDATED';

type CaseNoteSocketPayload = {
  id: string;
  caseId: string;
  timepoint?: string;
  eventTime?: string | Date;
  content: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  creatorNames: string;
};

export type CaseNoteSocketEvent = {
  type: CaseNoteEventType;
  caseIdentifier: string;
  payload: CaseNoteSocketPayload;
};

export const useConnnectionStore = defineStore('socket-connection', () => {
  const socket = getSocket();
  let exportEventsBound = false;
  let caseNoteEventsBound = false;
  let connectListenersBound = false;
  let subscriberId = 0;
  const caseNoteSubscribers = new Map<number, (event: CaseNoteSocketEvent) => void>();

  const normalizePayload = (data: unknown): CaseNoteSocketPayload | undefined => {
    if (typeof data !== 'object' || data === null) {
      return undefined;
    }

    const payload = data as Partial<CaseNoteSocketPayload>;
    if (typeof payload.caseId !== 'string' || payload.caseId.length === 0) {
      return undefined;
    }

    return payload as CaseNoteSocketPayload;
  };

  const publishCaseNoteEvent = (type: CaseNoteEventType, data: unknown) => {
    const payload = normalizePayload(data);
    if (!payload) {
      console.warn('[Socket] Dropping case note event — missing or invalid caseId. Raw data:', data);
      return;
    }

    console.debug(`[Socket] Publishing ${type} for case ${payload.caseId}. Subscribers: ${caseNoteSubscribers.size}`);
    const event: CaseNoteSocketEvent = {
      type,
      caseIdentifier: payload.caseId,
      payload
    };

    caseNoteSubscribers.forEach(callback => {
      try {
        callback(event);
      } catch (err) {
        console.error('[Socket] Case note subscriber threw:', err);
      }
    });
  };

  const bindCaseNoteEvents = () => {
    if (caseNoteEventsBound) {
      console.debug('[Socket] Case note events already bound, skipping.');
      return;
    }

    console.debug('[Socket] Binding CASE_NOTE_CREATED / CASE_NOTE_UPDATED listeners.');

    socket.on('CASE_NOTE_CREATED', data => {
      console.debug('[Socket] CASE_NOTE_CREATED received:', data);
      publishCaseNoteEvent('CASE_NOTE_CREATED', data);
    });

    socket.on('CASE_NOTE_UPDATED', data => {
      console.debug('[Socket] CASE_NOTE_UPDATED received:', data);
      publishCaseNoteEvent('CASE_NOTE_UPDATED', data);
    });

    caseNoteEventsBound = true;
  };

  const bindEvents = () => {
    if (exportEventsBound) {
      return;
    }

    socket.on('DATA_EXPORT_CREATED', data => {
      console.debug('[Socket] DATA_EXPORT_CREATED received:', data);
      const exportStore = useExportStore();
      exportStore.setExportFinishedStatus(data.identifier, data.error);
    });

    socket.on('disconnect', reason => {
      console.debug('[Socket] Disconnected:', reason);
    });

    exportEventsBound = true;
  };

  const connect = () => {
    if (!connectListenersBound) {
      socket.on('connect', () => {
        console.debug('[Socket] Connected. ID:', socket.id);
      });
      socket.on('connect_error', err => {
        console.error('[Socket] Connection error:', err.message, err);
      });
      connectListenersBound = true;
    }

    if (!socket.connected) {
      console.debug('[Socket] Connecting to', BACKEND_URL);
      socket.connect();
    } else {
      console.debug('[Socket] Already connected. ID:', socket.id);
    }
  };

  const subscribeToCaseNoteEvents = (
    callback: (event: CaseNoteSocketEvent) => void
  ) => {
    subscriberId += 1;
    const id = subscriberId;
    caseNoteSubscribers.set(id, callback);
    bindCaseNoteEvents();
    connect();

    return () => {
      caseNoteSubscribers.delete(id);
    };
  };

  return {
    connect,
    bindEvents,
    subscribeToCaseNoteEvents
  };
});
